import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MA_CATS, type CategoryId, type ImportedEvent } from "@myams/shared";
import {
  MaBottomNav, MaChipRow, MaCircleButton, MaImageSlot, MaTopBar,
} from "../components/ui";
import { Screen } from "../components/layout/Screen";
import { useImportedEvents } from "../lib/queries";

const TIME_BANDS = [
  { id: "morning",   label: "06–12", from: 6,  to: 12 },
  { id: "afternoon", label: "12–18", from: 12, to: 18 },
  { id: "evening",   label: "18–22", from: 18, to: 22 },
  { id: "night",     label: "22–04", from: 22, to: 28 },  // wraps past midnight
] as const;
type BandId = (typeof TIME_BANDS)[number]["id"];

const FILTER_CHIPS = [
  { id: "all",      label: "All" },
  { id: "music",    label: "Music" },
  { id: "art",      label: "Art" },
  { id: "outdoor",  label: "Outdoor" },
  { id: "food",     label: "Food" },
  { id: "free",     label: "Free" },
  { id: "located",  label: "On map" },
] as const;
type ChipId = (typeof FILTER_CHIPS)[number]["id"];

function inBand(secs: number | null | undefined, band: { from: number; to: number }): boolean {
  if (!secs) return false;
  const date = new Date(secs * 1000);
  const h = date.getUTCHours() + 1; // approximate Europe/Amsterdam offset (CEST: UTC+2; rough)
  const hr = h % 24;
  if (band.to <= 24) return hr >= band.from && hr < band.to;
  // wraps midnight
  return hr >= band.from || hr < band.to - 24;
}

export function NowFeed() {
  const navigate = useNavigate();
  const eventsQuery = useImportedEvents({ upcoming: true, limit: 200 });
  const { data: events = [], isPending } = eventsQuery;
  const [activeChip, setActiveChip] = useState<ChipId>("all");
  const [activeBand, setActiveBand] = useState<BandId | null>(null);

  const filtered = useMemo<ImportedEvent[]>(() => {
    let out = events;
    if (activeChip !== "all") {
      if (activeChip === "free") {
        out = out.filter((e) => /\bfree\b/i.test(e.description ?? "") || /\bfree\b/i.test(e.title ?? ""));
      } else if (activeChip === "located") {
        out = out.filter((e) => e.lat != null && e.lng != null);
      } else {
        out = out.filter((e) => e.category === (activeChip as CategoryId));
      }
    }
    if (activeBand) {
      const band = TIME_BANDS.find((b) => b.id === activeBand)!;
      out = out.filter((e) => inBand(e.startsAt, band));
    }
    return out;
  }, [events, activeChip, activeBand]);

  // Bucket by relative date.
  const buckets = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const today: ImportedEvent[] = [];
    const thisWeek: ImportedEvent[] = [];
    const later: ImportedEvent[] = [];
    for (const e of filtered) {
      const t = e.startsAt ?? null;
      if (!t) { thisWeek.push(e); continue; }
      const dt = t - now;
      if (dt < 86400) today.push(e);
      else if (dt < 86400 * 7) thisWeek.push(e);
      else later.push(e);
    }
    return [
      { title: "TODAY", items: today },
      { title: "THIS WEEK", items: thisWeek },
      { title: "LATER", items: later },
    ].filter((b) => b.items.length > 0);
  }, [filtered]);

  const totalToday = buckets.find((b) => b.title === "TODAY")?.items.length ?? 0;

  return (
    <Screen
      footer={<MaBottomNav active="now" />}
      // Pull-to-refresh: the cron updates every 6h so users will pull
      // when they want NOW-now. refetch returns a promise so the
      // spinner sticks until the new data lands.
      onPullToRefresh={async () => { await eventsQuery.refetch(); }}
    >
      <MaTopBar
        title="Tonight"
        subtitle={`${filtered.length} of ${events.length} · live from iamsterdam.com`}
        trailing={<MaCircleButton icon="filter" label="Reset filters" onClick={() => { setActiveChip("all"); setActiveBand(null); }} />}
      />

      {/* Time band rail — now actually filters */}
      <div style={{ padding: "0 16px 8px" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 0,
          background: "var(--ma-cream)",
          border: "var(--ma-border) solid var(--ma-ink)",
          borderRadius: "var(--ma-card-radius)",
          padding: 4, overflow: "hidden",
        }}>
          {TIME_BANDS.map((b) => {
            const on = activeBand === b.id;
            return (
              <button
                key={b.id}
                onClick={() => setActiveBand(on ? null : b.id)}
                style={{
                  flex: 1, padding: "8px 0", textAlign: "center",
                  background: on ? "var(--ma-ink)" : "transparent",
                  color: on ? "var(--ma-paper)" : "var(--ma-ink-soft)",
                  fontFamily: "var(--ma-mono)", fontSize: 12,
                  letterSpacing: "0.04em", fontWeight: 500,
                  border: "none", cursor: "pointer",
                }}>
                {b.label}
              </button>
            );
          })}
        </div>
        {activeBand && (
          <div className="ma-mono" style={{ fontSize: 9, color: "var(--ma-ink-soft)", letterSpacing: "0.1em", marginTop: 6 }}>
            tap again to clear time filter
          </div>
        )}
      </div>

      <MaChipRow
        items={FILTER_CHIPS.map((c) => ({ id: c.id, label: c.label, live: c.id === "all" }))}
        active={activeChip}
        onSelect={(id) => setActiveChip(id as ChipId)}
      />

      {isPending && (
        <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", padding: 24, textAlign: "center" }}>
          loading events…
        </div>
      )}

      {!isPending && filtered.length === 0 && (
        <div style={{ padding: 24, textAlign: "center" }}>
          <div className="ma-display" style={{ fontSize: 22, marginBottom: 6 }}>Nothing matches</div>
          <div style={{ fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink-soft)" }}>
            {events.length > 0 ? "Try clearing some filters." : "The next scrape runs every 6 hours."}
          </div>
        </div>
      )}

      <div style={{ padding: "10px 16px 0", position: "relative" }}>
        <div style={{
          position: "absolute", left: 24, top: 18, bottom: 0,
          width: 1, background: "var(--ma-ink)", opacity: 0.25,
        }} />
        {buckets.map((bucket, si) => (
          <div key={bucket.title} style={{ marginBottom: 22, position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{
                width: 16, height: 16,
                background: si === 0 ? "var(--ma-brick)" : "var(--ma-cream)",
                border: "var(--ma-border) solid var(--ma-ink)",
                position: "relative", zIndex: 2,
              }} />
              <div className="ma-mono" style={{ fontSize: 13, letterSpacing: "0.14em", color: "var(--ma-ink)", fontWeight: 600 }}>
                {bucket.title}
              </div>
              {si === 0 && totalToday > 0 && <span className="ma-pulse-dot" style={{ marginLeft: -2 }} />}
              <div style={{ flex: 1, height: 1, background: "var(--ma-ink)", opacity: 0.15 }} />
              <span className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.1em", color: "var(--ma-ink-soft)" }}>
                {bucket.items.length} happening
              </span>
            </div>

            <div style={{ marginLeft: 32, display: "flex", flexDirection: "column", gap: 12 }}>
              {bucket.items.slice(0, 12).map((e, i, arr) => {
                const cat = MA_CATS[e.category ?? "all"] ?? MA_CATS.all;
                // Siblings are scoped to the current bucket — swiping from
                // a TODAY event walks through TODAY items; same for THIS
                // WEEK / LATER. Matches what's visible in this column.
                const siblings = arr.map((x) => x.id);
                return (
                  <button
                    key={e.id}
                    onClick={() => navigate(`/event/${encodeURIComponent(e.id)}`, {
                      state: { siblings, index: i },
                    })}
                    style={{
                      display: "flex", gap: 12,
                      background: "var(--ma-cream)",
                      border: "var(--ma-border) solid var(--ma-ink)",
                      borderRadius: "var(--ma-card-radius)",
                      overflow: "hidden", padding: 0, textAlign: "left",
                      color: "inherit", cursor: "pointer", width: "100%",
                    }}>
                    <div style={{ width: 92, flexShrink: 0, position: "relative" }}>
                      <MaImageSlot
                        height="100%"
                        tone={e.category === "music" ? "brick" : e.category === "art" ? "canal" : "mustard"}
                        src={e.imageUrl ?? null}
                        category={e.category ?? null}
                        fallbackInitial={e.title}
                      />
                    </div>
                    <div style={{ flex: 1, padding: "12px 14px 12px 0", minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span className="ma-mono" style={{
                          fontSize: 9, letterSpacing: "0.14em",
                          textTransform: "uppercase", color: cat.color, fontWeight: 600,
                        }}>
                          {cat.label}
                        </span>
                      </div>
                      <div className="ma-display" style={{ fontSize: 17, color: "var(--ma-ink)", lineHeight: 1.1, marginBottom: 4 }}>
                        {e.title}
                      </div>
                      {e.description && (
                        <div style={{
                          fontFamily: "var(--ma-body)", fontSize: 12,
                          color: "var(--ma-ink-soft)", lineHeight: 1.3,
                          display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden",
                        }}>
                          {e.description}
                        </div>
                      )}
                      {e.neighborhood && (
                        <div className="ma-mono" style={{ fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ma-ink-soft)", marginTop: 4 }}>
                          {e.neighborhood}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

    </Screen>
  );
}
